import moment from 'moment';

import { FIXED_RATE_TYPE, MEMBERSHIP_PAY_PERIOD_OPTIONS } from '~/app_constants';

// Pay period: the form holds the option id ('0'/'1'/…), the serializer holds the option key
// ('weekly'/'monthly'/…). Both are normalised to display text so they can be compared and shown.
const periodTextById = (id) => MEMBERSHIP_PAY_PERIOD_OPTIONS.find((o) => o.id === id)?.text || 'None';
const periodTextByKey = (key) => MEMBERSHIP_PAY_PERIOD_OPTIONS.find((o) => o.key === key)?.text || 'None';

// Rate type: the form holds the option id ('1' = fixed), the serializer holds the key ('fixed_amount').
const unitByTypeId = (id) => (id === FIXED_RATE_TYPE ? 'fixed' : 'hour');
const unitByTypeKey = (key) => (key === 'fixed_amount' ? 'fixed' : 'hour');

const toAmount = (value) => {
  const num = Number(value);

  return Number.isFinite(num) && num > 0 ? num : null;
};

const buildSection = ({ key, showPeriod, current, updated, today }) => {
  // Nothing to review when there is neither an existing rate nor a newly entered one.
  if (!current && updated.amount == null) { return null; }

  // The effective-date field stays null until the user picks a date; the save then defaults the new
  // rate to today. So the day a value change actually takes effect is `picked || today`.
  const updatedStartIso = updated.startDateIso || today;

  const changed = {
    amount: !current || current.amount !== updated.amount,
    unit: !current || current.unit !== updated.unit,
    period: showPeriod && (!current || current.period !== updated.period),
    // Highlighted only when the new rate takes effect on a different day than the current one.
    date: !current || !moment(updatedStartIso).isSame(moment(current.startDateIso), 'day'),
  };

  const valueChanged = changed.amount || changed.unit || changed.period;
  // A date-only edit is only persisted when it moves the rate earlier than the current start (it
  // overrides the current rate); a later/default date with no value change is dropped on save, so it
  // is not a reviewable change.
  const overridesByEarlierDate = !!current && moment(updatedStartIso).isBefore(moment(current.startDateIso), 'day');

  if (!valueChanged && !overridesByEarlierDate) { return null; }

  return { key, showPeriod, current, updated: { ...updated, startDateIso: updatedStartIso }, changed };
};

// Builds the CURRENT → UPDATED comparison model the rate review dialog renders. Returns one entry
// per rate type (pay/bill) that actually changed; an empty array means there is nothing to review.
export const rateReviewSections = (payments, today = moment().format('YYYY-MM-DD')) => {
  const currentRates = payments.current_rates || {};
  const isFixed = payments.rate_type === FIXED_RATE_TYPE;
  const sections = [];

  // --- Pay rate ---
  const payCurrentRaw = currentRates.pay_rate;
  const paySection = buildSection({
    key: 'pay',
    showPeriod: true,
    current: payCurrentRaw?.rate != null
      ? {
          amount: toAmount(payCurrentRaw.rate),
          unit: unitByTypeKey(payCurrentRaw.type),
          period: periodTextByKey(payCurrentRaw.pay_period),
          startDateIso: payCurrentRaw.start_date_iso,
        }
      : null,
    updated: {
      amount: isFixed ? toAmount(payments.fixed_amount) : toAmount(payments.pay_rate),
      unit: unitByTypeId(payments.rate_type),
      period: periodTextById(payments.pay_period),
      startDateIso: payments.pay_rate_start_date,
    },
    today,
  });

  if (paySection) { sections.push(paySection); }

  // --- Bill rate ---
  const billCurrentRaw = currentRates.bill_rate;
  const billSection = buildSection({
    key: 'bill',
    showPeriod: false,
    current: billCurrentRaw?.rate != null
      ? {
          amount: toAmount(billCurrentRaw.rate),
          unit: 'hour',
          period: null,
          startDateIso: billCurrentRaw.start_date_iso,
        }
      : null,
    updated: {
      amount: toAmount(payments.bill_rate),
      unit: 'hour',
      period: null,
      startDateIso: payments.bill_rate_start_date,
    },
    today,
  });

  if (billSection) { sections.push(billSection); }

  return sections;
};
